import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, ClipboardList, MessageSquare, NotebookPen, Sun, Zap, Hourglass, Sparkles,
  CircleCheck, Rocket, Calendar, CornerDownRight, Search, RefreshCw, Lightbulb, CalendarDays,
} from 'lucide-react';
import { apiClient } from '../../packages/api-client';
import { StateView } from '../../packages/ui';
import { getAIStatus, type AIStatus, type AIUnavailableReason } from '../../packages/api-client/ai-credentials-api';

interface PlannedTask {
  title: string;
  description?: string;
  phase?: string;
  priority?: string;
  due_date?: string;
  subtasks?: PlannedTask[];
}
interface PlanResponse {
  project_name: string;
  summary: string;
  tasks: PlannedTask[];
  created?: boolean;
}
interface BriefingResponse {
  due_today: number;
  overdue: number;
  blocked: number;
  suggestions: string[];
  summary: string;
}
interface ImpactResponse {
  affected_tasks: string[];
  launch_slip_days: number;
  suggestions: string[];
  summary: string;
}

type Tab = 'plan' | 'ask' | 'notes' | 'brief' | 'impact';

interface AIPilotProps {
  projectId: string;
  projectName: string;
}

/** Why the co-pilot can't run, from GET /ai/status. */
const UNAVAILABLE: Record<Exclude<AIUnavailableReason, ''>, { reason: string; nextStep: React.ReactNode }> = {
  disabled: {
    reason: 'AI is switched off for this KaziAtlas deployment.',
    nextStep: 'The operator of this deployment can turn it back on.',
  },
  not_configured: {
    reason: 'No Anthropic API key is set up for this workspace.',
    nextStep: <>A workspace admin can add one in <Link to="/admin/ai">AI settings</Link>.</>,
  },
  quota_exceeded: {
    reason: "This workspace has used this month's free AI allowance.",
    nextStep: <>A workspace admin can add the workspace's own Anthropic key in <Link to="/admin/ai">AI settings</Link>.</>,
  },
};

export function AIPilot({ projectId, projectName }: AIPilotProps) {
  const [tab, setTab] = useState<Tab>('plan');
  // null while checking; 'unknown' when the check itself failed (the tabs still
  // render, with a notice, since each call reports its own failure).
  const [status, setStatus] = useState<AIStatus | 'unknown' | null>(null);
  useEffect(() => {
    getAIStatus()
      .then(setStatus)
      .catch(() => setStatus('unknown'));
  }, []);

  if (status === null) return <StateView state="loading" label="Checking AI availability" />;
  if (status !== 'unknown' && !status.available) {
    const why = (status.reason && UNAVAILABLE[status.reason]) || { reason: 'AI is not available right now.', nextStep: 'Try again later.' };
    return <StateView state="denied" title="AI Co-Pilot is unavailable" reason={why.reason} nextStep={why.nextStep} />;
  }

  return (
    <div className="ai-pilot">
      <div className="ai-pilot-header">
        <div className="ai-pilot-title">
          <span className="ai-pilot-icon"><Bot size={24} aria-hidden="true" /></span>
          <div>
            <h2>AI Co-Pilot</h2>
            <p className="page-subtitle">Powered by Claude · {projectName}</p>
          </div>
        </div>
      </div>

      {status === 'unknown' && (
        <p role="alert" className="ai-error">Couldn't check whether AI is available; a request may fail.</p>
      )}

      <div className="ai-pilot-tabs">
        {([
          { id: 'plan',   label: <><ClipboardList size={15} aria-hidden="true" /> Generate Plan</> },
          { id: 'ask',    label: <><MessageSquare size={15} aria-hidden="true" /> Ask the Project</> },
          { id: 'notes',  label: <><NotebookPen size={15} aria-hidden="true" /> Meeting Notes</> },
          { id: 'brief',  label: <><Sun size={15} aria-hidden="true" /> Daily Brief</> },
          { id: 'impact', label: <><Zap size={15} aria-hidden="true" /> Dependency Impact</> },
        ] as { id: Tab; label: React.ReactNode }[]).map(t => (
          <button
            key={t.id}
            className={`ai-tab-btn${tab === t.id ? ' active' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="ai-pilot-body">
        {tab === 'plan'   && <GeneratePlanTab projectId={projectId} />}
        {tab === 'ask'    && <AskProjectTab projectId={projectId} />}
        {tab === 'notes'  && <MeetingNotesTab projectId={projectId} />}
        {tab === 'brief'  && <DailyBriefTab />}
        {tab === 'impact' && <DependencyImpactTab projectId={projectId} />}
      </div>
    </div>
  );
}

// ── Generate Plan Tab ──────────────────────────────────────────────────────────

function GeneratePlanTab({ projectId }: { projectId: string }) {
  const [prompt, setPrompt] = useState('');
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setError(''); setPlan(null); setCreated(false);
    try {
      const { data } = await apiClient.post<PlanResponse>(
        `/projects/${projectId}/ai/generate-plan`,
        { prompt, create: false }
      );
      setPlan(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to generate plan');
    } finally { setLoading(false); }
  };

  const createTasks = async () => {
    if (!prompt.trim()) return;
    setCreating(true);
    try {
      await apiClient.post(`/projects/${projectId}/ai/generate-plan`, { prompt, create: true });
      setCreated(true);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to create tasks');
    } finally { setCreating(false); }
  };

  return (
    <div className="ai-tab-content">
      <p className="ai-hint">Describe your project in plain language and the AI will generate a full task plan with phases, subtasks, and due dates.</p>
      <div className="ai-examples">
        <span>Try:</span>
        {['Launch a mobile app in 3 months', 'Open a coffee shop', 'Run a marketing campaign'].map(ex => (
          <button key={ex} className="ai-example-chip" onClick={() => setPrompt(ex)}>{ex}</button>
        ))}
      </div>
      <textarea
        className="ai-textarea"
        aria-label="Project goal"
        placeholder="e.g. Launch a mobile app in 3 months with a team of 4 developers..."
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        rows={4}
      />
      <div className="ai-actions">
        <button className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={generate} disabled={loading || !prompt.trim()}>
          {loading ? <><Hourglass size={16} aria-hidden="true" /> Generating…</> : <><Sparkles size={16} aria-hidden="true" /> Generate Plan</>}
        </button>
      </div>

      {error && <div className="ai-error">{error}</div>}

      {plan && (
        <div className="ai-plan-result">
          <div className="ai-plan-header">
            <div>
              <h3>{plan.project_name}</h3>
              <p className="ai-plan-summary">{plan.summary}</p>
            </div>
            <button className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={createTasks} disabled={creating || created}>
              {created ? <><CircleCheck size={16} aria-hidden="true" /> Tasks Created!</> : creating ? <><Hourglass size={16} aria-hidden="true" /> Creating…</> : <><Rocket size={16} aria-hidden="true" /> Create {plan.tasks.length} Tasks</>}
            </button>
          </div>

          <div className="ai-plan-tasks">
            {plan.tasks.map((task, i) => (
              <div key={i} className="ai-plan-task-card">
                <div className="ai-plan-task-header">
                  {task.phase && <span className="ai-phase-badge">{task.phase}</span>}
                  <span className={`priority-badge priority-${task.priority ?? 'medium'}`}>{task.priority ?? 'medium'}</span>
                  {task.due_date && <span className="ai-due-date" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={12} aria-hidden="true" /> {task.due_date}</span>}
                </div>
                <div className="ai-plan-task-title">{task.title}</div>
                {task.description && <div className="ai-plan-task-desc">{task.description}</div>}
                {task.subtasks && task.subtasks.length > 0 && (
                  <div className="ai-subtask-list">
                    {task.subtasks.map((sub, j) => (
                      <div key={j} className="ai-subtask-item">
                        <span className="ai-subtask-bullet"><CornerDownRight size={12} aria-hidden="true" /></span> {sub.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ask the Project Tab ────────────────────────────────────────────────────────

function AskProjectTab({ projectId }: { projectId: string }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ask = async () => {
    if (!question.trim()) return;
    setLoading(true); setError(''); setAnswer('');
    try {
      const { data } = await apiClient.post<{ answer: string }>(
        `/projects/${projectId}/ai/ask`,
        { question }
      );
      setAnswer(data.answer);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to analyse project');
    } finally { setLoading(false); }
  };

  return (
    <div className="ai-tab-content">
      <p className="ai-hint">Ask any question about this project's current state, blockers, or progress.</p>
      <div className="ai-examples">
        <span>Try:</span>
        {['What is blocking this project?', 'Which tasks are overdue?', 'What should we focus on next?'].map(ex => (
          <button key={ex} className="ai-example-chip" onClick={() => setQuestion(ex)}>{ex}</button>
        ))}
      </div>
      <div className="ai-ask-row">
        <input
          className="input"
          placeholder="Ask a question about this project..."
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ask()}
        />
        <button className="btn btn-primary" aria-label="Ask" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={ask} disabled={loading || !question.trim()}>
          {loading ? <Hourglass size={16} aria-hidden="true" /> : <><MessageSquare size={16} aria-hidden="true" /> Ask</>}
        </button>
      </div>
      {error && <div className="ai-error">{error}</div>}
      {answer && (
        <div className="ai-answer-card">
          <div className="ai-answer-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Bot size={15} aria-hidden="true" /> AI Analysis</div>
          <div className="ai-answer-body" style={{ whiteSpace: 'pre-wrap' }}>{answer}</div>
        </div>
      )}
    </div>
  );
}

// ── Meeting Notes Tab ──────────────────────────────────────────────────────────

function MeetingNotesTab({ projectId }: { projectId: string }) {
  const [notes, setNotes] = useState('');
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState('');

  const parse = async () => {
    if (!notes.trim()) return;
    setLoading(true); setError(''); setPlan(null); setCreated(false);
    try {
      const { data } = await apiClient.post<PlanResponse>(
        `/projects/${projectId}/ai/meeting-notes`,
        { notes, create: false }
      );
      setPlan(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to parse notes');
    } finally { setLoading(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      await apiClient.post(`/projects/${projectId}/ai/meeting-notes`, { notes, create: true });
      setCreated(true);
    } finally { setCreating(false); }
  };

  return (
    <div className="ai-tab-content">
      <p className="ai-hint">Paste your meeting notes and the AI will extract all action items and decisions as tasks.</p>
      <textarea
        className="ai-textarea"
        aria-label="Meeting notes"
        placeholder={`Paste your meeting notes here…\n\nExample:\n- Discuss API security review\n- Add audit logs by Friday\n- Launch beta on May 20\n- John to handle payment integration`}
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={8}
      />
      <div className="ai-actions">
        <button className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={parse} disabled={loading || !notes.trim()}>
          {loading ? <><Hourglass size={16} aria-hidden="true" /> Parsing…</> : <><Search size={16} aria-hidden="true" /> Extract Tasks</>}
        </button>
      </div>
      {error && <div className="ai-error">{error}</div>}
      {plan && (
        <div className="ai-plan-result">
          <div className="ai-plan-header">
            <div>
              <h3>Extracted {plan.tasks.length} action items</h3>
              <p className="ai-plan-summary">{plan.summary}</p>
            </div>
            <button className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={create} disabled={creating || created}>
              {created ? <><CircleCheck size={16} aria-hidden="true" /> Tasks Created!</> : creating ? <><Hourglass size={16} aria-hidden="true" /> Creating…</> : <><Rocket size={16} aria-hidden="true" /> Add to Project</>}
            </button>
          </div>
          <div className="ai-plan-tasks">
            {plan.tasks.map((task, i) => (
              <div key={i} className="ai-plan-task-card">
                <div className="ai-plan-task-header">
                  <span className={`priority-badge priority-${task.priority ?? 'medium'}`}>{task.priority ?? 'medium'}</span>
                  {task.due_date && <span className="ai-due-date" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={12} aria-hidden="true" /> {task.due_date}</span>}
                </div>
                <div className="ai-plan-task-title">{task.title}</div>
                {task.description && <div className="ai-plan-task-desc">{task.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Daily Brief Tab ────────────────────────────────────────────────────────────

function DailyBriefTab() {
  const [brief, setBrief] = useState<BriefingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.get<BriefingResponse>('/ai/briefing');
      setBrief(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load briefing');
    } finally { setLoading(false); }
  };

  React.useEffect(() => { load(); }, []);

  return (
    <div className="ai-tab-content">
      {loading && <div className="ai-loading" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Hourglass size={16} aria-hidden="true" /> Loading your daily brief…</div>}
      {error && <div className="ai-error">{error}</div>}
      {brief && (
        <div className="ai-brief-card">
          <div className="ai-brief-header">
            <span className="ai-brief-icon"><Sun size={20} aria-hidden="true" /></span>
            <div>
              <h3>Your Daily Brief</h3>
              <span className="ai-brief-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
          <div className="ai-brief-stats">
            <div className={`ai-stat ${brief.due_today > 0 ? 'warning' : 'ok'}`}>
              <div className="ai-stat-num">{brief.due_today}</div>
              <div className="ai-stat-label">Due Today</div>
            </div>
            <div className={`ai-stat ${brief.overdue > 0 ? 'danger' : 'ok'}`}>
              <div className="ai-stat-num">{brief.overdue}</div>
              <div className="ai-stat-label">Overdue</div>
            </div>
            <div className={`ai-stat ${brief.blocked > 0 ? 'warning' : 'ok'}`}>
              <div className="ai-stat-num">{brief.blocked}</div>
              <div className="ai-stat-label">Blocked</div>
            </div>
          </div>
          {brief.summary && (
            <div className="ai-brief-summary">{brief.summary}</div>
          )}
          {brief.suggestions.length > 0 && (
            <div className="ai-brief-suggestions">
              <div className="ai-brief-suggestions-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Lightbulb size={15} aria-hidden="true" /> Suggested Actions</div>
              <ul>
                {brief.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <RefreshCw size={14} aria-hidden="true" /> Refresh
          </button>
        </div>
      )}
    </div>
  );
}

// ── Dependency Impact Tab ──────────────────────────────────────────────────────

function DependencyImpactTab({ projectId }: { projectId: string }) {
  const [taskId, setTaskId] = useState('');
  const [slipDays, setSlipDays] = useState(5);
  const [impact, setImpact] = useState<ImpactResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const analyse = async () => {
    if (!taskId.trim()) return;
    setLoading(true); setError(''); setImpact(null);
    try {
      const { data } = await apiClient.post<ImpactResponse>(
        `/projects/${projectId}/ai/dependency-impact`,
        { task_id: taskId, slip_days: slipDays }
      );
      setImpact(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to analyse impact');
    } finally { setLoading(false); }
  };

  return (
    <div className="ai-tab-content">
      <p className="ai-hint">Enter a Task ID and estimated slip to see the cascade impact on the rest of your project.</p>
      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '1rem' }}>
        <div className="form-group">
          <label className="form-label">Task ID</label>
          <input className="input" aria-label="Task ID" placeholder="Paste a Task ID from the Kanban board…" value={taskId} onChange={e => setTaskId(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Slip (days)</label>
          <input className="input" type="number" min={1} max={90} value={slipDays} onChange={e => setSlipDays(Number(e.target.value))} />
        </div>
      </div>
      <button className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }} onClick={analyse} disabled={loading || !taskId.trim()}>
        {loading ? <><Hourglass size={16} aria-hidden="true" /> Analysing…</> : <><Zap size={16} aria-hidden="true" /> Analyse Impact</>}
      </button>
      {error && <div className="ai-error">{error}</div>}
      {impact && (
        <div className="ai-impact-card">
          <div className="ai-impact-summary">{impact.summary}</div>
          <div className="ai-impact-slip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CalendarDays size={15} aria-hidden="true" /> Estimated launch slip: <strong>{impact.launch_slip_days} days</strong>
          </div>
          {impact.affected_tasks.length > 0 && (
            <div className="ai-impact-affected">
              <div className="ai-impact-label">Affected tasks:</div>
              <ul>{impact.affected_tasks.map((t, i) => <li key={i}>• {t}</li>)}</ul>
            </div>
          )}
          {impact.suggestions.length > 0 && (
            <div className="ai-impact-suggestions">
              <div className="ai-impact-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Lightbulb size={15} aria-hidden="true" /> Options:</div>
              {impact.suggestions.map((s, i) => (
                <div key={i} className="ai-suggestion-card">
                  <span className="ai-suggestion-num">Option {i + 1}</span> {s}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
